import { useState, useEffect } from 'react';
import { Event, Invite } from '../lib/types';
import { format } from 'date-fns';

interface EventCardProps {
  event: Event;
  invite?: Invite;
  onOpenMap: () => void;
  onRsvpClick: () => void;
}

export default function EventCard({ event, invite, onOpenMap, onRsvpClick }: EventCardProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const formatDate = (date: Date) => {
    return format(new Date(date), 'EEEE, MMM d');
  };

  const formatTime = (date: Date) => {
    return format(new Date(date), 'h:mm a');
  };

  // Memoize timezone abbreviation to avoid hydration mismatch
  // Use the visitor's local timezone since date-fns format() displays times in local timezone
  const [timezoneAbbr, setTimezoneAbbr] = useState<string>('');
  
  useEffect(() => {
    try {
      const date = new Date(event.event_date);
      // Don't specify timeZone - this uses the visitor's local timezone
      const abbr = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short'
      }).formatToParts(date).find(part => part.type === 'timeZoneName')?.value || '';
      setTimezoneAbbr(abbr);
    } catch {
      setTimezoneAbbr('');
    }
  }, [event.event_date]);

  const formatTimeRange = () => {
    const startTime = formatTime(event.event_date);
    
    if (event.event_date_end) {
      const endTime = formatTime(event.event_date_end);
      return `${startTime} - ${endTime}${timezoneAbbr ? ` ${timezoneAbbr}` : ''}`;
    }
    return `${startTime}${timezoneAbbr ? ` ${timezoneAbbr}` : ''}`;
  };

  const getRsvpStatusInfo = () => {
    if (!invite) {
      return {
        text: 'No Response',
        bgColor: 'bg-orange-100',
        textColor: 'text-orange-700'
      };
    }

    const additionalAdults = invite.invite_guest || 0;
    const childCount = invite.invite_childcount || 0;
    
    const formatGuestCount = () => {
      // Base count is 1 adult (the invitee) + additional adults
      const totalAdults = 1 + additionalAdults;
      const parts = [];
      
      parts.push(`${totalAdults} ${totalAdults === 1 ? 'Adult' : 'Adults'}`);
      
      if (childCount > 0) {
        parts.push(`${childCount} ${childCount === 1 ? 'Child' : 'Children'}`);
      }
      
      return ` - ${parts.join(', ')}`;
    };

    switch (invite.invite_status) {
      case 'attending':
        return {
          text: `Attending${formatGuestCount()}`,
          bgColor: 'bg-green-100',
          textColor: 'text-green-700'
        };
      case 'notattending':
        return {
          text: 'Not Attending',
          bgColor: 'bg-red-100',
          textColor: 'text-red-700'
        };
      case 'maybe':
        return {
          text: 'Maybe',
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700'
        };
      case 'opened':
      case 'notopened':
        return {
          text: 'No Response',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700'
        };
      default:
        return {
          text: 'No Response',
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700'
        };
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4">
      <div className="bg-white rounded-lg shadow p-6 -mt-8 relative z-10">
      {/* Event Title */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-gray-900">
          {event.event_name}
        </h1>
        {invite?.id === 'preview' && (
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs px-2 py-1 rounded-full font-medium">
            PREVIEW
          </span>
        )}
      </div>
      
      {/* Host Information: always show */}
      <p className="text-gray-500 text-base mb-2">
        Hosted by: {event.hosted_by && event.hosted_by.trim() !== ''
          ? event.hosted_by
          : `${event.user_objectId?.firstname ?? ''} ${event.user_objectId?.lastname ?? ''}`.trim() || 'Host'}
      </p>

      {/* Date and Time */}
      <div className="flex items-start space-x-3 mb-2">
        <div className="flex-shrink-0 mt-1">
          <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="text-gray-900 font-semibold text-lg">
            {formatDate(event.event_date)}
          </p>
          <p className="text-gray-600 text-base">
            {formatTimeRange()}
          </p>
        </div>
      </div>

      {/* RSVP Status */}
      <div className="flex items-center justify-between">
        <span className={`${getRsvpStatusInfo().bgColor} ${getRsvpStatusInfo().textColor} px-3 py-1 rounded-full text-sm font-medium`}>
          {getRsvpStatusInfo().text}
        </span>
        <button
          onClick={onRsvpClick}
          className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors"
        >
          Edit RSVP
        </button>
      </div>
      </div>
    </div>
  );
}
